// frontend/src/pages/Sales.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { fmtDate as fmtTz } from '../utils/time';

// helpers
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmtMoney = (n) => toNum(n).toFixed(2);

export default function Sales() {
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // pagination (client-side)
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const v = Number(localStorage.getItem('sales.rowsPerPage'));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });

  async function load(q = '', srt = 'date_desc') {
    const params = {};
    if (q && q.trim()) params.search = q.trim();
    if (srt) params.sort = srt;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    // fetch all and paginate on the client
    params.limit = 'all';

    const resp = await api.get('/sales', { params });
    setList(resp.data || []);
  }

  // initial load
  useEffect(() => { load(); }, []);

  // debounce filters & sort
  useEffect(() => {
    const t = setTimeout(() => load(search, sort), 300);
    return () => clearTimeout(t);
  }, [search, sort, fromDate, toDate]);

  // reset page on filters change
  useEffect(() => { setPage(1); }, [search, sort, fromDate, toDate]);

  // pagination bits
  const totalRows = list?.length || 0;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(totalRows / rowsPerPage)),
    [totalRows, rowsPerPage]
  );

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const onChangeRowsPerPage = (e) => {
    const v = Number(e.target.value || 25);
    localStorage.setItem('sales.rowsPerPage', String(v));
    setRowsPerPage(v);
    setPage(1);
  };

  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const pageRows = useMemo(() => list.slice(startIdx, endIdx), [list, startIdx, endIdx]);

  const gotoFirst = () => setPage(1);
  const gotoPrev  = () => setPage((p) => Math.max(1, p - 1));
  const gotoNext  = () => setPage((p) => Math.min(pageCount, p + 1));
  const gotoLast  = () => setPage(pageCount);

  return (
    <div className='container'>
      <div className='toolbar' style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <h2 style={{ marginRight:'auto' }}>Sales</h2>

        <input
          type="text"
          value={search}
          onChange={(e)=>setSearch(e.target.value)}
          placeholder='Search invoice, customer, phone, GSTIN'
          style={{ padding: 10, boxSizing: 'border-box', flex: '1 1 600px', minWidth: 360 }}
        />

        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          <label style={{ fontSize:12 }}>From</label>
          <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} />
          <label style={{ fontSize:12 }}>To</label>
          <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} />
          {(fromDate || toDate) && (
            <button className="btn" onClick={()=>{ setFromDate(''); setToDate(''); }}>
              Clear
            </button>
          )}
        </div>

        <select value={sort} onChange={(e)=>setSort(e.target.value)} style={{ padding:8, flexShrink:0 }}>
          <option value='date_desc'>Date: Newest → Oldest</option>
          <option value='date_asc'>Date: Oldest → Newest</option>
          <option value='customer_asc'>Customer: A → Z</option>
          <option value='customer_desc'>Customer: Z → A</option>
          <option value='total_desc'>Total: High → Low</option>
          <option value='total_asc'>Total: Low → High</option>
        </select>
      </div>

      <div className='card' style={{ overflowX:'auto', marginTop: '12px' }}>
        <table className='table'>
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Total</th>{/* already net of refunds (sales.total) */}
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id}>
                <td>{r.invoice_no}</td>
                <td>{fmtTz(r.created_at)}</td>
                <td>{r.customer_name || '-'}</td>
                <td>{r.customer_phone || '-'}</td>
                <td style={{ fontWeight: 600 }}>{fmtMoney(r.total)}</td>
                <td>{r.payment_method || '-'}</td>
                <td>
                  <Link className='btn btn-sm' to={`/sales/${r.id}`}>View</Link>
                </td>
              </tr>
            ))}
            {!list.length && (
              <tr><td colSpan={7} style={{ textAlign:'center', padding:24 }}>No sales</td></tr>
            )}
          </tbody>
        </table>

        {/* Pager */}
        <div className="flex" style={{
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderTop: '1px solid var(--border, #e5e7eb)',
          flexWrap: 'wrap'
        }}>
          <div className="flex" style={{ alignItems:'center', gap:8 }}>
            <label className="muted" htmlFor="rows-per-page">Rows/page</label>
            <select
              id="rows-per-page"
              className="input"
              value={rowsPerPage}
              onChange={onChangeRowsPerPage}
              style={{ width: 96 }}
              aria-label="Rows per page"
            >
              {[10,25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="muted" style={{ marginLeft: 12 }}>
              {totalRows
                ? `${startIdx + 1}–${Math.min(endIdx, totalRows)} of ${totalRows}`
                : '0 of 0'}
            </span>
          </div>

          <div className="flex" style={{ alignItems:'center', gap:6, flexWrap: 'wrap' }}>
            <button className="btn btn-sm secondary" onClick={gotoFirst} disabled={page <= 1} aria-label="First page">«</button>
            <button className="btn btn-sm secondary" onClick={gotoPrev}  disabled={page <= 1} aria-label="Previous page">‹</button>
            <span className="muted" style={{ padding: '0 6px' }}>
              Page <b>{page}</b> / {pageCount}
            </span>
            <button className="btn btn-sm secondary" onClick={gotoNext}  disabled={page >= pageCount} aria-label="Next page">›</button>
            <button className="btn btn-sm secondary" onClick={gotoLast}  disabled={page >= pageCount} aria-label="Last page">»</button>
          </div>
        </div>
      </div>
    </div>
  );
}
